import { CORE_API_BASE as API_URL } from "#app/lib/api-bases";
import { FORBIDDEN_CODES } from "#app/lib/utils/is-forbidden-error";
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache, HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { toastManager } from "@salestrak/ui";
import { getMainDefinition } from "@apollo/client/utilities";
import type { FieldPolicy } from "@apollo/client";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { finalize } from "rxjs";
import type { Omittable } from "@salestrak/ui";
import { heldPhotoFieldIds } from "#app/lib/photo-field-holds";

/**
 * Per-page caching for offset-paginated connections.
 *
 * Each (filterArgs, skip, limit) tuple gets its own cache entry. We do NOT
 * merge pages into one big sparse array — Apollo's InMemoryCache compacts
 * holes on read, so `merged[1600]=…` would emit length 1 instead of 1601.
 * Cross-page assembly is the consumer's job (DataTable maintains a
 * pageStart -> rows map keyed by absolute offset). User entities stay
 * normalized via `keyFields: ["userId"]`, so memory cost is just the
 * per-page array of `__ref`s, not duplicated User documents.
 *
 * `merge: true` — two queries on the same key tuple (the board's
 * WorkflowStagesData selects `total`, the list's issue query does not) write
 * one unnormalized connection object. Without a merge the second write
 * REPLACES it, dropping the fields only the first selected (Apollo's "Cache
 * data may be lost when replacing the stages field" warning). Merging keeps
 * them; `edges` is still replaced whole, and its rows are normalized refs.
 */
function offsetPagination(filterArgs: string[]): FieldPolicy {
  return {
    keyArgs: [...filterArgs, "skip", "limit"],
    merge: true,
  };
}

/**
 * Append-style pagination for the global-search `{ total, items }` sections.
 *
 * Unlike `offsetPagination` (random-access tables — separate cache entry per
 * page, assembled by the consumer), global search's "load more" is strictly
 * SEQUENTIAL: each page is fetched at `offset = rows already shown`, so a
 * concatenating merge produces a dense array with no holes (the sparse-array
 * compaction problem that rules out merges for random access can't occur here).
 * `keyArgs` excludes `offset`/`limit` so every page of one query text merges
 * into a single growing list the watching query reads whole; a new query text
 * (or scope) is a fresh entry that starts empty. Without this, growing `limit`
 * re-fetched every prior page on each "load more" (O(n²)).
 */
function searchSectionPagination(keyArgs: string[]): FieldPolicy {
  return {
    keyArgs,
    merge(existing, incoming, { args }) {
      const offset: number = args?.offset ?? 0;
      const existingItems: unknown[] = existing?.items ?? [];
      // Clamp the write start to the current length. An offset past the end
      // (e.g. "load more" fired while a new query's first page hasn't landed)
      // would otherwise leave `undefined` holes; a base-page refetch (offset 0
      // under cache-and-network) rebuilds from an empty tail so stale or
      // duplicated rows from an earlier page can't linger past a shrunk result.
      const start = Math.min(offset, existingItems.length);
      const items = existingItems.slice(0, start);
      const incomingItems = incoming?.items ?? [];
      for (let i = 0; i < incomingItems.length; i++) {
        items[start + i] = incomingItems[i];
      }
      // `total` (and __typename) always come from the freshest page.
      return { ...incoming, items };
    },
  };
}

const cache = new InMemoryCache({
  possibleTypes: {
    TrashItem: ["Issue"],
    RestoreTrashItemResult: ["Issue", "NotFoundError", "PermissionDenied"],
    DeleteTrashItemResult: ["DeleteTrashItemSuccess", "NotFoundError", "PermissionDenied"],
    ClearAllTrashResult: ["ClearAllTrashSuccess", "PermissionDenied"],
    CreateIssueResult: ["Issue", "PermissionDenied", "ValidationError"],
    UpdateIssueResult: ["Issue", "NotFoundError", "PermissionDenied", "ValidationError"],
    DeleteIssueResult: ["Issue", "NotFoundError", "PermissionDenied"],
  },
  typePolicies: {
    Issue: {
      keyFields: ["issueId"],
      fields: {
        // A photos field this tab is mid-write on keeps its local value
        // (lib/photo-field-holds.ts). A merge function can't read `issueId`,
        // so `read` stashes it in the per-entity `storage`.
        values: {
          read(existing, { readField, storage }) {
            if (storage.issueId === undefined) storage.issueId = readField<string>("issueId");
            return existing;
          },
          merge(existing, incoming, { storage }) {
            const issueId = storage.issueId as string | undefined;
            const held = issueId ? heldPhotoFieldIds(issueId) : [];
            if (held.length === 0 || !existing || !incoming) return incoming;
            const local = existing as Record<string, unknown>;
            const values: Record<string, unknown> = { ...(incoming as Record<string, unknown>) };
            for (const fieldId of held) {
              if (fieldId in local) values[fieldId] = local[fieldId];
              else delete values[fieldId];
            }
            return values;
          },
        },
        assignees: { merge: (_, incoming) => incoming },
        members: { merge: (_, incoming) => incoming },
        teams: { merge: (_, incoming) => incoming },
        notes: { merge: (_, incoming) => incoming },
      },
    },
    Icon: { keyFields: false, merge: true },
    // A share row's `id` is the user or team id, but its level, switch and
    // source belong to ONE subject. Normalized by `id`, a person's row on
    // one workflow, project or dashboard overwrote their row on every other
    // subject. Kept inline in the summary that returned it.
    WorkflowAccessMember: { keyFields: false },
    WorkflowAccessTeam: { keyFields: false },
    ItemAccessMember: { keyFields: false },
    ItemAccessTeam: { keyFields: false },
    Stage: { keyFields: ["stageId"] },
    Workflow: {
      keyFields: ["workflowId"],
      fields: {
        stages: { merge: (_, incoming) => incoming },
        issueTypes: { merge: (_, incoming) => incoming },
      },
    },
    Team: {
      keyFields: ["teamId"],
      fields: {
        icon: { merge: (_, incoming) => incoming },
        leads: { merge: (_, incoming) => incoming },
        members: { merge: (_, incoming) => incoming },
      },
    },
    User: {
      keyFields: ["userId"],
      fields: {
        roles: { merge: (_, incoming) => incoming },
        teams: { merge: (_, incoming) => incoming },
      },
    },
    Note: {
      keyFields: ["noteId"],
      fields: {
        replies: { merge: (_, incoming) => incoming },
        reactions: { merge: (_, incoming) => incoming },
      },
    },
    IssueType: { keyFields: ["issueTypeId"] },
    CustomField: { keyFields: ["customFieldId"] },
    Role: { keyFields: ["roleId"] },
    Address: { keyFields: ["addressId"] },
    Filter: { keyFields: ["filterId"] },
    Workspace: { keyFields: ["workspaceId"] },
    Activity: { keyFields: ["activityId"] },
    Notification: { keyFields: ["inboxId"] },
    // Normalized by member so PresenceChanged fragment writes update the
    // GetPresences snapshot rows in place (no poll, no manual list surgery).
    UserPresence: { keyFields: ["userId"] },
    ApiKey: { keyFields: ["apiKeyId"] },
    Webhook: { keyFields: ["webhookId"] },
    UserPreferences: {
      keyFields: ["userId", "workspaceId"],
      fields: {
        favorites: { merge: (_, incoming) => incoming },
        infoBoxes: { merge: (_, incoming) => incoming },
        tabCollections: { merge: (_, incoming) => incoming },
        collapsedSideMenuItems: { merge: (_, incoming) => incoming },
        railTiles: { merge: (_, incoming) => incoming },
        homeSectionOrder: { merge: (_, incoming) => incoming },
        collapsedSummarySections: { merge: (_, incoming) => incoming },
        workflowOrder: { merge: (_, incoming) => incoming },
      },
    },
    Query: {
      fields: {
        issues: offsetPagination(["where"]),
        stages: offsetPagination(["where", "orderBy"]),
        users: offsetPagination(["where", "orderBy"]),
        teams: offsetPagination(["where", "orderBy"]),
        workflows: offsetPagination(["where", "orderBy"]),
        notesByIssue: offsetPagination(["issueId"]),
        formsByIssue: offsetPagination(["issueId"]),
        activities: offsetPagination(["where", "orderBy"]),
        notes: offsetPagination(["where"]),
        notifications: offsetPagination(["showCleared", "where"]),
        searchIssues: searchSectionPagination(["query", "scopeWorkflowId"]),
        searchUsers: searchSectionPagination(["query"]),
        searchTeams: searchSectionPagination(["query"]),
        searchAddresses: searchSectionPagination(["query"]),
      },
    },
  },
});

function createAuthLink(getAccessToken: () => Promise<string | null>, getWorkspaceId: () => string | null) {
  return setContext(async (_, { headers }) => {
    const token = await getAccessToken();
    const workspaceId = getWorkspaceId();
    return {
      headers: {
        ...headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(workspaceId ? { workspaceid: workspaceId } : {}),
      },
    };
  });
}

function createErrorLink(onUnauthenticated?: () => void) {
  let signingOut = false;
  // Coalesce per-operation 403 toasts so a single rejected mutation
  // doesn't show multiple toasts if the server emits more than
  // one GraphQL error.
  let lastForbiddenToastAt = 0;
  return new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const { message, locations, path, extensions } of error.errors) {
        if (extensions?.code === "UNAUTHENTICATED") {
          const ctx = operation.getContext();
          if (!ctx.headers?.authorization) continue;
          if (onUnauthenticated && !signingOut) {
            signingOut = true;
            onUnauthenticated();
            setTimeout(() => {
              signingOut = false;
            }, 2000);
          }
          continue;
        }
        // Backend rejects unauthorized actions with `FORBIDDEN`
        // (Nest ForbiddenException → 403). The PermissionGuard also
        // attaches more specific codes — FORBIDDEN_CODES is the one
        // list shared with isForbiddenError(), which per-mutation
        // handlers use to skip their own toast. Throttled so a single
        // mutation with multiple errors only fires one toast.
        if (
          typeof extensions?.code === "string" &&
          FORBIDDEN_CODES.has(extensions.code)
        ) {
          const now = performance.now();
          if (now - lastForbiddenToastAt > 1000) {
            lastForbiddenToastAt = now;
            toastManager.add({ title: "No access", description: "You can't perform this action.", type: "error" });
          }
          continue;
        }
        console.warn(`[GraphQL error]: ${message}`, { locations, path, operation: operation.operationName });
      }
    } else {
      // Anything that is not a GraphQL-error envelope is a transport-level
      // failure. Demote fetch-level failures to warn so the console isn't
      // noisy when the device has transient connectivity issues.
      const msg = String((error as Error).message ?? error);
      const isFetchFailure = /load failed|network|failed to fetch|networkerror/i.test(msg);
      const log = isFetchFailure ? console.warn : console.error;
      log(`[Network error]: ${msg}`, {
        operation: operation.operationName,
      });
    }
  });
}

let _wsClient: ReturnType<typeof createClient> | null = null;

function buildWsClient(url: string, getAccessToken: () => Promise<string | null>, getWorkspaceId: () => string | null) {
  return createClient({
    url,
    retryAttempts: Infinity,
    // iOS WKWebView reports silent socket deaths as generic Error, not
    // CloseEvent. Retry unconditionally.
    shouldRetry: () => true,
    keepAlive: 10_000,
    connectionAckWaitTimeout: 10_000,
    retryWait: async (retries) => {
      const base = Math.min(1000 * 2 ** retries, 5_000);
      const jitter = Math.random() * 1000;
      await new Promise((resolve) => setTimeout(resolve, base + jitter));
    },
    connectionParams: async () => {
      const token = await getAccessToken();
      const workspaceId = getWorkspaceId();
      return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(workspaceId ? { WorkspaceId: workspaceId } : {}),
      };
    },
  });
}

export function restartWsConnection() {
  _wsClient?.terminate();
}

// Mutations this tab has sent and not yet seen answered. LiveWorkspaceSync
// holds its refetch while one is out: a refetch that lands before the write
// commits reads the old value and overwrites the optimistic one.
let inFlightMutations = 0;
export function mutationsInFlight(): number {
  return inFlightMutations;
}

const mutationCountLink = new ApolloLink((operation, forward) => {
  const def = getMainDefinition(operation.query);
  if (def.kind !== "OperationDefinition" || def.operation !== "mutation") {
    return forward(operation);
  }
  inFlightMutations += 1;
  return forward(operation).pipe(
    finalize(() => {
      inFlightMutations -= 1;
    }),
  );
});

export type CreateApolloClientOptions = Omittable<{
  getAccessToken: () => Promise<string | null>;
  getWorkspaceId: () => string | null;
  onUnauthenticated?: () => void;
}>;

export function createApolloClient({ getAccessToken, getWorkspaceId, onUnauthenticated }: CreateApolloClientOptions) {
  const httpLink = new HttpLink({ uri: `${API_URL}/graphql` });
  const authLink = createAuthLink(getAccessToken, getWorkspaceId);
  const errorLink = createErrorLink(onUnauthenticated);

  // graphql-ws mounts at the same /graphql path as HTTP (NestJS Apollo
  // driver default).
  const wsUrl = `${API_URL.replace(/^http/, "ws")}/graphql`;
  _wsClient = buildWsClient(wsUrl, getAccessToken, getWorkspaceId);
  const wsLink = new GraphQLWsLink(_wsClient);

  const splitLink = ApolloLink.split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === "OperationDefinition" && def.operation === "subscription";
    },
    wsLink,
    ApolloLink.from([mutationCountLink, errorLink, authLink, httpLink]),
  );

  return new ApolloClient({
    link: splitLink,
    cache,

    defaultOptions: {
      watchQuery: { fetchPolicy: "cache-and-network", errorPolicy: "all" },
      query: { fetchPolicy: "network-only", errorPolicy: "all" },
      mutate: { errorPolicy: "all" },
    },
  });
}

export { cache };

// `errorPolicy: "all"` above must also be DECLARED here: the declaration is
// what makes every query/mutation result type carry the `| undefined` data
// that policy really produces, so call sites are forced to handle partial
// results instead of discovering them at runtime. The client does not use
// `@client` or `@defer`, so there is no `localState`, no `incrementalHandler`
// and no `TypeOverrides` augmentation.
/* eslint-disable @typescript-eslint/no-namespace, no-restricted-syntax --
   Apollo publishes `DeclareDefaultOptions` as nested namespaces, and the
   interface names (`Query`, `Mutate`) are fixed by that API — they name
   client methods, not schema types. */
declare module "@apollo/client" {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      // Affects client.watchQuery() and the React hooks (useQuery etc.).
      interface WatchQuery {
        errorPolicy: "all";
      }
      // Affects client.query().
      interface Query {
        errorPolicy: "all";
      }
      // Affects client.mutate() and useMutation().
      interface Mutate {
        errorPolicy: "all";
      }
    }
  }
}
/* eslint-enable @typescript-eslint/no-namespace, no-restricted-syntax */

